/**
 * Checks if all required fields in the application steps are completed.
 *
 * This function iterates through the provided `horizontalSteps` structure,
 * and for each required field that is not removed, verifies if the corresponding
 * value exists in the `fetchUserApplicationData` summary data.
 *
 * @param horizontalSteps - An array representing the application's horizontal steps,
 *   each containing vertical steps and their fields.
 * @param fetchUserApplicationData - An array containing the user's application data,
 *   structured similarly to `horizontalSteps`, used to check if fields are filled.
 * @returns `true` if all required fields are filled; otherwise, `false`.
 */
export function isApplicationCompleted(
  horizontalSteps: any[],
  fetchUserApplicationData: any
) {
  // Defensive: if fetchUserApplicationData is an array, use first element
  const summaryData = Array.isArray(fetchUserApplicationData)
    ? fetchUserApplicationData[0]
    : fetchUserApplicationData;
  const summaryHorizontalSteps = summaryData?.horizontalSteps || [];

  // Helper to get value for a field from summary API data
  function getFieldValue(verticalStepName: any, fieldName: any) {
    for (const hStep of summaryHorizontalSteps) {
      for (const vStep of hStep.verticalSteps) {
        if (vStep.verticalStepName === verticalStepName) {
          for (const fieldObj of vStep.fields) {
            if (
              fieldObj[fieldName] !== undefined &&
              fieldObj[fieldName] !== ""
            ) {
              return true;
            }
          }
        }
      }
    }
    return false;
  }

  for (const hStep of horizontalSteps) {
    for (const vStep of hStep.verticalSteps) {
      for (const field of vStep.fields) {
        if (field.required && !field.isRemoved) {
          if (!getFieldValue(vStep.stepLabel, field.name)) {
            return false; // Found a required field not filled
          }
        }
      }
    }
  }
  return true; // All required fields are filled
}

// Helper to convert camelCase to 'Title Case'
export function camelToTitle(str: string) {
  return str
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

/**
 * Constants for Step Names
 * Only the constants need updating if the API step names change.
 */
const STEP_NAME = {
  ELIGIBILITY_CHECK: "Eligibility Check",
  APPLICANT_DETAILS: "Applicant & Loan Details",
  PROVISIONAL_OFFER: "Provisional Offer Letter",
  ADDITIONAL_DETAILS_AND_DOCUMENTS: "Additional Details & Documents",
  FINAL_VERIFICATION_AND_SANCTION: "Final Verification & Sanction",
  DISBURSEMENT_OF_FUNDS: "Disbursement of Funds",
} as const;

interface Field {
  [key: string]: any;
}
interface VerticalStep {
  fields: Field[];
}

interface HorizontalStep {
  completionRate: number;
  horizontalStepName: string;
  verticalStepsCount: number;
  verticalSteps: VerticalStep[];
}

interface Application {
  horizontalSteps: HorizontalStep[];
  submissionId: string;
  createdAt: string;
  updatedAt: string;
  isProvisionalGenerated?: boolean;
  isQualified?: boolean;
  isEligibilityChecked?: any;
  partnerStartedJourney?: boolean;
  status?:string;
  applicationStatus?: string;
}

interface CombinedStep {
  label: string;
  completionRate: number;
  status: "pending" | "completed" | "inprogress";
  isActive: boolean;
  isCompleted: boolean;
  isProvisionalGenerated?: boolean;
  isQualified?: boolean;
  isEligible?: any;
  applicationStatus?: string;
}

/**
 * Returns GA event prefix for Eligibility Check based on application source.
 */
export function getGAEligibilityStatements(applicationSource: string) {
  switch (applicationSource) {
    case "CRM":
      return "CRMINIELIGICHECK ";
    case "LOS":
      return "LOSINIELIGICHECK";
    default:
      return "CUSINIELIGICHECK";
  }
}

/**
 * Returns GA event prefix for Provisional Offer based on application source.
 */
export function getGAProvisionalOfferStatements(applicationSource: string) {
  switch (applicationSource) {
    case "CRM":
      return "CRMINIPROVISONOFF";
    case "LOS":
      return "LOSINIPROVISONOFF";
    default:
      return "CUSINIPROVISONOFF";
  }
}

/**
 * Calculates the application progress based on dynamic horizontal steps and vertical step counts.
 *
 * @param selectedApp The application data with dynamic horizontal steps and step counts.
 * @param applicationStatus The external application status string (optional: can be undefined, null, or empty string).
 * @returns An array of CombinedStep objects with progress and status.
 */
export function getCombinedApplicationSteps(
  selectedApp: Application,
  applicationStatu?: string
): CombinedStep[] {
  const combinedSteps: CombinedStep[] = [];
  if (!Array.isArray(selectedApp?.horizontalSteps) || selectedApp.horizontalSteps.length === 0) {
    // Fallback: show all steps, first step in progress, others greyed out
    return [
      {
        label: STEP_NAME.ELIGIBILITY_CHECK,
        completionRate: 0,
        status: "inprogress",
        isActive: true,
        isCompleted: false,
        isProvisionalGenerated: false,
        isQualified: false,
        isEligible: null,
      },
      {
        label: STEP_NAME.APPLICANT_DETAILS,
        completionRate: 0,
        status: "pending",
        isActive: false,
        isCompleted: false,
        isProvisionalGenerated: false,
        isQualified: false,
        isEligible: null,
      },
      {
        label: STEP_NAME.PROVISIONAL_OFFER,
        completionRate: 0,
        status: "pending",
        isActive: false,
        isCompleted: false,
        isProvisionalGenerated: false,
        isQualified: false,
        isEligible: null,
      },
      {
        label: STEP_NAME.ADDITIONAL_DETAILS_AND_DOCUMENTS,
        completionRate: 0,
        status: "pending",
        isActive: false,
        isCompleted: false,
        isProvisionalGenerated: false,
        isQualified: false,
        isEligible: null,
      },
      {
        label: STEP_NAME.FINAL_VERIFICATION_AND_SANCTION,
        completionRate: 0,
        status: "pending",
        isActive: false,
        isCompleted: false,
        isProvisionalGenerated: false,
        isQualified: false,
        isEligible: null,
      },
      {
        label: STEP_NAME.DISBURSEMENT_OF_FUNDS,
        completionRate: 0,
        status: "pending",
        isActive: false,
        isCompleted: false,
        isProvisionalGenerated: false,
        isQualified: false,
        isEligible: null,
      },
    ];
  }
  
  // --- 1. Create the base combinedSteps array based on dynamic data ---
  for (let i = 0; i < selectedApp.horizontalSteps.length; i++) {
    const dynamicStep = selectedApp.horizontalSteps[i];
    if (!dynamicStep || typeof dynamicStep !== 'object' || !dynamicStep.horizontalStepName) {
      // Skip undefined or malformed steps
      continue;
    }
    const stepLabel = dynamicStep.horizontalStepName;
    // Set completionRate to 100 by default if applicationStatus is "disbursed"
    let completionPercentage = selectedApp?.status?.toLowerCase() === "disbursed" 
      ? 100 
      : (typeof dynamicStep.completionRate === 'number' ? dynamicStep.completionRate : 0);

    let isCurrentStepComplete = false;
    if (dynamicStep.horizontalStepName === "Eligibility Check") {
      isCurrentStepComplete = selectedApp?.isEligibilityChecked === true ? true : selectedApp?.isQualified === true ? true : false;
    }
    if (dynamicStep.horizontalStepName === "Applicant & Loan Details") {
      isCurrentStepComplete = selectedApp?.isProvisionalGenerated === true ? true : false;
    }
    const stepStatus = isCurrentStepComplete ? "completed" : "pending";

    const previousStepRate =
      selectedApp.horizontalSteps[i - 1] && typeof selectedApp.horizontalSteps[i - 1].completionRate === 'number'
        ? selectedApp.horizontalSteps[i - 1].completionRate
        : 0;
    const isPreviousStepComplete = previousStepRate === 100;

    // A step is Active if it is not completed AND (it's the first step OR the previous step is completed)
    const isActive =
      !isCurrentStepComplete && (i === 0 || isPreviousStepComplete);
    // Set isEligible based on isEligibilityChecked:
    // - false: not eligible (show cross)
    // - null: in-progress (show number with color)
    // - true/undefined: eligible (normal behavior)
    let isEligibleSuccess = selectedApp?.isEligibilityChecked === false ? false : 
      selectedApp?.isEligibilityChecked === null ? null : true;
    combinedSteps.push({
      label: stepLabel,
      completionRate: completionPercentage,
      status: stepStatus,
      isActive: isActive,
      isCompleted: isCurrentStepComplete,
      isProvisionalGenerated: selectedApp.isProvisionalGenerated,
      isQualified: selectedApp.isQualified,
      isEligible: isEligibleSuccess,
      applicationStatus: selectedApp?.status?.toLowerCase(),
    });
  }

  // Remove any undefined or null steps just in case
  const filteredCombinedSteps = combinedSteps.filter(Boolean);
  // Fallback: if all steps are blank/undefined, show all steps with first in progress
  if (filteredCombinedSteps.length === 0) {
    return [
      {
        label: STEP_NAME.ELIGIBILITY_CHECK,
        completionRate: 0,
        status: "inprogress",
        isActive: true,
        isCompleted: false,
        isProvisionalGenerated: false,
        isQualified: false,
        isEligible: null,
        applicationStatus: selectedApp?.status?.toLowerCase(),
      },
      {
        label: STEP_NAME.APPLICANT_DETAILS,
        completionRate: 0,
        status: "pending",
        isActive: false,
        isCompleted: false,
        isProvisionalGenerated: false,
        isQualified: false,
        isEligible: null,
        applicationStatus: selectedApp?.status?.toLowerCase(),
      },
      {
        label: STEP_NAME.PROVISIONAL_OFFER,
        completionRate: 0,
        status: "pending",
        isActive: false,
        isCompleted: false,
        isProvisionalGenerated: false,
        isQualified: false,
        isEligible: null,
        applicationStatus: selectedApp?.status?.toLowerCase(),
      },
      {
        label: STEP_NAME.ADDITIONAL_DETAILS_AND_DOCUMENTS,
        completionRate: 0,
        status: "pending",
        isActive: false,
        isCompleted: false,
        isProvisionalGenerated: false,
        isQualified: false,
        isEligible: null,
        applicationStatus: selectedApp?.status?.toLowerCase(),
      },
      {
        label: STEP_NAME.FINAL_VERIFICATION_AND_SANCTION,
        completionRate: 0,
        status: "pending",
        isActive: false,
        isCompleted: false,
        isProvisionalGenerated: false,
        isQualified: false,
        isEligible: null,
        applicationStatus: selectedApp?.status?.toLowerCase(),
      },
      {
        label: STEP_NAME.DISBURSEMENT_OF_FUNDS,
        completionRate: 0,
        status: "pending",
        isActive: false,
        isCompleted: false,
        isProvisionalGenerated: false,
        isQualified: false,
        isEligible: null,
        applicationStatus: selectedApp?.status?.toLowerCase(),
      },
    ];
  }

  // For debugging
  // --- 1.5. Handle special case: isEligibilityChecked == null and isQualified == false ---
  // In this case, keep eligibility stage in-progress and gray out all other stages
  if (selectedApp.isEligibilityChecked === null && selectedApp.isQualified === false) {
    const eligibilityIndex = combinedSteps && combinedSteps?.findIndex(
      (step) => step.label.toLowerCase() === STEP_NAME.ELIGIBILITY_CHECK.toLowerCase()
    );
    
    // Mark eligibility as in-progress and all other stages as pending with 0% completion
    for (let i = 0; i < combinedSteps.length; i++) {
      if (i === eligibilityIndex) {
        // Keep eligibility stage in-progress
        combinedSteps[i] = {
          ...combinedSteps[i],
          isActive: true,
          isCompleted: false,
          isEligible: null, // Set to null to show it's in-progress but not eligible yet
        };
      } else {
        // Gray out all other stages
        combinedSteps[i] = {
          ...combinedSteps[i],
          completionRate: 0,
          status: "pending",
          isActive: false,
          isCompleted: false,
          isEligible: null, // Set to null to show it's not accessible yet
        };
      }
    }
    
    // Return early to skip other logic
    return combinedSteps;
  }

  // --- 1.6. Handle case: isEligibilityChecked == null (regardless of isQualified) ---
  // Ensure eligibility stage shows as in-progress (not completed) with number displayed
  if (selectedApp.isEligibilityChecked === null) {
    const eligibilityIndex = combinedSteps && combinedSteps?.findIndex(
      (step) => step.label.toLowerCase() === STEP_NAME.ELIGIBILITY_CHECK.toLowerCase()
    );
    
    if (eligibilityIndex !== -1) {
      // Ensure eligibility is not marked as completed
      combinedSteps[eligibilityIndex] = {
        ...combinedSteps[eligibilityIndex],
        completionRate: combinedSteps[eligibilityIndex].completionRate === 100 ? 50 : combinedSteps[eligibilityIndex].completionRate,
        isCompleted: false,
        isActive: true,
        isEligible: null, // This ensures the step shows number with color
      };
    }
  }

  // --- 2. isQualified Logic: If isQualified is true, mark Eligibility as complete and Applicant Details as in-progress ---
  if (selectedApp.isQualified === true) {
    const eligibilityIndex = combinedSteps && combinedSteps?.findIndex(
      (step) => step.label === STEP_NAME.ELIGIBILITY_CHECK
    );
    
    const applicantDetailsIndex = combinedSteps && combinedSteps?.findIndex(
      (step) => step.label === STEP_NAME.APPLICANT_DETAILS
    );
    // Mark Eligibility Check as completed (ticked)
    if (eligibilityIndex !== -1) {
      combinedSteps[eligibilityIndex] = {
        ...combinedSteps[eligibilityIndex],
        completionRate: 100,
        status: "completed",
        isActive: false,
        isCompleted: true,
      };
    }

    // Mark Applicant Details as in-progress (only if not already completed)
    if (applicantDetailsIndex !== -1) {
      const isApplicantDetailsComplete = combinedSteps[applicantDetailsIndex].completionRate === 100;
      if(selectedApp?.isProvisionalGenerated === true){
          combinedSteps[applicantDetailsIndex] = {
            ...combinedSteps[applicantDetailsIndex],
            isActive: false,
            isCompleted: true,
          };
      }else{
        if (!isApplicantDetailsComplete) {
          combinedSteps[applicantDetailsIndex] = {
            ...combinedSteps[applicantDetailsIndex],
            isActive: true,
            isCompleted: false,
          };
        }
      }
    }
  }

  // --- 3. Partner Journey Logic: Override completion for specific steps ---
  if (selectedApp.partnerStartedJourney === true) {
    // Find the indices of the steps that need to be marked as completed
    const eligibilityIndex = combinedSteps && combinedSteps?.findIndex(
      (step) => step.label === STEP_NAME.ELIGIBILITY_CHECK
    );
    const applicantDetailsIndex = combinedSteps && combinedSteps?.findIndex(
      (step) => step.label === STEP_NAME.APPLICANT_DETAILS
    );
    const provisionalOfferIndex = combinedSteps && combinedSteps?.findIndex(
      (step) => step.label === STEP_NAME.PROVISIONAL_OFFER
    );
    const additionalDetailsIndex = combinedSteps && combinedSteps?.findIndex(
      (step) => step.label === STEP_NAME.ADDITIONAL_DETAILS_AND_DOCUMENTS
    );
    const finalVerificationIndex = combinedSteps && combinedSteps?.findIndex(
      (step) => step.label === STEP_NAME.FINAL_VERIFICATION_AND_SANCTION
    );

    // Mark first 4 steps as completed (ticked)
    const stepsToComplete = [
      eligibilityIndex,
      applicantDetailsIndex,
      provisionalOfferIndex,
      additionalDetailsIndex
    ];

    stepsToComplete.forEach((stepIndex) => {
      if (stepIndex !== -1) {
        combinedSteps[stepIndex] = {
          ...combinedSteps[stepIndex],
          completionRate: 100,
          status: "completed",
          isActive: false,
          isCompleted: true,
        };
      }
    });

    // Mark Final Verification & Sanction as in-progress
    if (finalVerificationIndex !== -1) {
      combinedSteps[finalVerificationIndex] = {
        ...combinedSteps[finalVerificationIndex],
        completionRate: 50, // In-progress state (between 0 and 100)
        status: "pending",
        isActive: true,
        isCompleted: false,
      };
    }
  }

  // --- 3. Handle eligibility completion and provisional offer logic ---
  const eligibilityCheckStep = combinedSteps && combinedSteps?.find(
    (step) =>
      step.label === STEP_NAME.ELIGIBILITY_CHECK
  );

  const applicantDetailsStep = combinedSteps && combinedSteps?.find(
    (step) =>
      step.label === STEP_NAME.APPLICANT_DETAILS
  );

  // Scenario (a): If eligibility is complete (100%), ensure Applicant/Loan Details is active (in progress)
  // This ensures proper progression regardless of lead source (customer or 3rd party)
  // const applicantCompletionRate = applicantDetailsStep?.completionRate ?? 0;
  // if (eligibilityCheckStep?.completionRate === 100 && applicantCompletionRate < 100) {
  //   const applicantDetailsIndex = combinedSteps && combinedSteps?.findIndex(
  //     (step) => step.label === STEP_NAME.APPLICANT_DETAILS
  //   );
    
  //   if (applicantDetailsIndex !== -1) {
  //     combinedSteps[applicantDetailsIndex] = {
  //       ...combinedSteps[applicantDetailsIndex],
  //       isActive: true,
  //       isCompleted: false,
  //     };
  //   }
  // }

  const arePrerequisiteStepsCompleted =
    eligibilityCheckStep?.completionRate === 100 &&
    applicantDetailsStep?.completionRate === 100;

  // Scenario (b): If provisional offer is generated (positive or negative), mark Provisional Offer as complete
  // and Additional Details as in-progress - based ONLY on isProvisionalGenerated flag
  if (selectedApp.isProvisionalGenerated === true) {
    const provisionalOfferStepIndex = combinedSteps && combinedSteps?.findIndex(
      (step) => step.label === STEP_NAME.PROVISIONAL_OFFER
    );

    // Mark Provisional Offer as completed (ticked)
    if (provisionalOfferStepIndex !== -1) {
      combinedSteps[provisionalOfferStepIndex] = {
        ...combinedSteps[provisionalOfferStepIndex],
        completionRate: 100,
        status: "completed",
        isActive: false,
        isCompleted: true,
        isProvisionalGenerated: selectedApp.isProvisionalGenerated,
        isQualified: selectedApp.isQualified,
        isEligible: selectedApp?.isEligibilityChecked===false ? false : true,
      };
    }

    // Mark Additional Details as in-progress
    const additionalDetailsIndex = combinedSteps && combinedSteps?.findIndex(
      (step) => step.label === STEP_NAME.ADDITIONAL_DETAILS_AND_DOCUMENTS
    );
    
    if (additionalDetailsIndex !== -1) {
      const isAdditionalDetailsComplete = combinedSteps[additionalDetailsIndex].completionRate === 100;
      
      // Only mark as active if not already completed
      if (!isAdditionalDetailsComplete) {
        combinedSteps[additionalDetailsIndex] = {
          ...combinedSteps[additionalDetailsIndex],
          isActive: true,
          isCompleted: false,
          isProvisionalGenerated: selectedApp.isProvisionalGenerated,
          isQualified: selectedApp.isQualified,
          isEligible: selectedApp?.isEligibilityChecked===false ? false : true,
        };
      }
    }
  }

  // --- 5. Override status based on external applicationStatus (Final Verification & Sanction) ---
  const finalVerificationIndex = combinedSteps && combinedSteps?.findIndex(
    (step) => step.label === STEP_NAME.FINAL_VERIFICATION_AND_SANCTION
  );
  const disbursementIndex = combinedSteps && combinedSteps?.findIndex(
    (step) => step.label === STEP_NAME.DISBURSEMENT_OF_FUNDS
  );
  const additionalDetailsIndex = combinedSteps && combinedSteps?.findIndex(
    (step) => step.label === STEP_NAME.ADDITIONAL_DETAILS_AND_DOCUMENTS
  );

  // Check if a valid applicationStatus is present and if prerequisites are met
  const isExternalStatusValid = applicationStatu && applicationStatu.length > 0;

  if (finalVerificationIndex !== -1 && isExternalStatusValid) {
    const finalVerificationStep = combinedSteps[finalVerificationIndex];

    switch (selectedApp?.status?.toLowerCase()) {
      case "submit_to_cpa":
        if (additionalDetailsIndex !== -1) {
          combinedSteps[additionalDetailsIndex] = {
            ...combinedSteps[additionalDetailsIndex],
            isActive: false,
            isCompleted: true,
            completionRate: 100,
            status: "completed",
            isProvisionalGenerated: selectedApp.isProvisionalGenerated,
            isQualified: selectedApp.isQualified,
            isEligible: selectedApp?.isEligibilityChecked===false ? false : true,
            applicationStatus: selectedApp?.status?.toLowerCase(),
          };
        }
        combinedSteps[finalVerificationIndex] = {
          ...finalVerificationStep,
          isActive: true,
          isCompleted: false,
          completionRate: 0,
          status: "pending",
          isProvisionalGenerated: selectedApp.isProvisionalGenerated,
          isQualified: selectedApp.isQualified,
          isEligible: selectedApp?.isEligibilityChecked===false ? false : true,
          applicationStatus: selectedApp?.status?.toLowerCase(),
        };
        if (disbursementIndex !== -1) {
          combinedSteps[disbursementIndex] = {
            ...combinedSteps[disbursementIndex],
            isActive: false,
            isCompleted: false,
            completionRate: 0,
            status: "pending",
            isProvisionalGenerated: selectedApp.isProvisionalGenerated,
            isQualified: selectedApp.isQualified,
            isEligible: selectedApp?.isEligibilityChecked===false ? false : true,
            applicationStatus: selectedApp?.status?.toLowerCase(),
          };
        }
        break;
      case "sanctioned":
        const eligibilityIndex = combinedSteps && combinedSteps?.findIndex(
          (step) => step.label === STEP_NAME.ELIGIBILITY_CHECK
        );
        const applicantIndex = combinedSteps && combinedSteps?.findIndex(
          (step) => step.label === STEP_NAME.APPLICANT_DETAILS
        );
        const provisionalIndex = combinedSteps && combinedSteps?.findIndex(
          (step) => step.label === STEP_NAME.PROVISIONAL_OFFER
        );
        [eligibilityIndex, applicantIndex, provisionalIndex].forEach((index) => {
          if (index !== -1) {
            combinedSteps[index] = {
              ...combinedSteps[index],
              isActive: false,
              isCompleted: true,
              completionRate: 100,
              status: "completed",
              isProvisionalGenerated: selectedApp.isProvisionalGenerated,
              isQualified: selectedApp.isQualified,
              isEligible: selectedApp?.isEligibilityChecked===false ? false : true,
            applicationStatus: selectedApp?.status?.toLowerCase(),
            };
          }
        });
        if (additionalDetailsIndex !== -1) {
          combinedSteps[additionalDetailsIndex] = {
            ...combinedSteps[additionalDetailsIndex],
            isActive: false,
            isCompleted: true,
            completionRate: 100,
            status: "completed",
            isProvisionalGenerated: selectedApp.isProvisionalGenerated,
            isQualified: selectedApp.isQualified,
            isEligible: selectedApp?.isEligibilityChecked===false ? false : true,
          };
        }
        combinedSteps[finalVerificationIndex] = {
          ...finalVerificationStep,
          isActive: false,
          isCompleted: true,
          completionRate: 100,
          status: "completed",
          isProvisionalGenerated: selectedApp.isProvisionalGenerated,
          isQualified: selectedApp.isQualified,
          isEligible: selectedApp?.isEligibilityChecked===false ? false : true,
        };
        if (disbursementIndex !== -1) {
          combinedSteps[disbursementIndex] = {
            ...combinedSteps[disbursementIndex],
            isActive: true,
            isCompleted: false,
            completionRate: 50,
            status: "inprogress",
            isProvisionalGenerated: selectedApp.isProvisionalGenerated,
            isQualified: selectedApp.isQualified,
            isEligible: selectedApp?.isEligibilityChecked===false ? false : true,
            applicationStatus: selectedApp?.status?.toLowerCase(),
          };
        }
        break;
      case "disbursed":
        // Mark all steps as completed (ticked)
        // Mark all steps as completed (ticked) - ensure all steps are processed
        const allStepIndices = [
          combinedSteps.findIndex((step) => step.label === STEP_NAME.ELIGIBILITY_CHECK),
          combinedSteps.findIndex((step) => step.label === STEP_NAME.APPLICANT_DETAILS),
          combinedSteps.findIndex((step) => step.label === STEP_NAME.PROVISIONAL_OFFER),
          combinedSteps.findIndex((step) => step.label === STEP_NAME.ADDITIONAL_DETAILS_AND_DOCUMENTS),
          combinedSteps.findIndex((step) => step.label === STEP_NAME.FINAL_VERIFICATION_AND_SANCTION),
          combinedSteps.findIndex((step) => step.label === STEP_NAME.DISBURSEMENT_OF_FUNDS)
        ];
        
        allStepIndices.forEach((index) => {
          if (index !== -1) {
            combinedSteps[index] = {
              ...combinedSteps[index],
              isActive: false,
              isCompleted: true,
              completionRate: 100,
              status: "completed",
              isProvisionalGenerated: selectedApp.isProvisionalGenerated,
              isQualified: selectedApp.isQualified,
              isEligible: selectedApp?.isEligibilityChecked === false ? false : true,
              applicationStatus: selectedApp?.status?.toLowerCase(),
            };
          }
        });
        break;
    }
  }
  if(selectedApp?.status?.toLowerCase()==="disbursed")
  {
     const allStepIndices = [
          combinedSteps.findIndex((step) => step.label === STEP_NAME.ELIGIBILITY_CHECK),
          combinedSteps.findIndex((step) => step.label === STEP_NAME.APPLICANT_DETAILS),
          combinedSteps.findIndex((step) => step.label === STEP_NAME.PROVISIONAL_OFFER),
          combinedSteps.findIndex((step) => step.label === STEP_NAME.ADDITIONAL_DETAILS_AND_DOCUMENTS),
          combinedSteps.findIndex((step) => step.label === STEP_NAME.FINAL_VERIFICATION_AND_SANCTION),
          combinedSteps.findIndex((step) => step.label === STEP_NAME.DISBURSEMENT_OF_FUNDS)
        ];
        
    allStepIndices.forEach((index) => {
      if (index !== -1) {
        combinedSteps[index] = {
          ...combinedSteps[index],
          isActive: false,
          isCompleted: true,
          completionRate: 100,
          status: "completed",
          isProvisionalGenerated: selectedApp.isProvisionalGenerated,
          isQualified: selectedApp.isQualified,
          isEligible: selectedApp?.isEligibilityChecked === false ? false : true,
          applicationStatus: selectedApp?.status?.toLowerCase(),
        };
      }
    });
  }
  if(selectedApp?.status?.toLowerCase()==="sanctioned")
  {
     const indices = [
        combinedSteps.findIndex((step) => step.label === STEP_NAME.ELIGIBILITY_CHECK),
        combinedSteps.findIndex((step) => step.label === STEP_NAME.APPLICANT_DETAILS),
        combinedSteps.findIndex((step) => step.label === STEP_NAME.PROVISIONAL_OFFER),
        combinedSteps.findIndex((step) => step.label === STEP_NAME.ADDITIONAL_DETAILS_AND_DOCUMENTS),
        combinedSteps.findIndex((step) => step.label === STEP_NAME.FINAL_VERIFICATION_AND_SANCTION)
      ];
      indices.forEach((index) => {
        if (index !== -1) {
          combinedSteps[index] = {
            ...combinedSteps[index],
            isActive: false,
            isCompleted: true,
            completionRate: 100,
            status: "completed",
            isProvisionalGenerated: selectedApp.isProvisionalGenerated,
            isQualified: selectedApp.isQualified,
            isEligible: selectedApp?.isEligibilityChecked===false ? false : true,
            applicationStatus: selectedApp?.status?.toLowerCase(),
          };
        }
      });
      if (disbursementIndex !== -1) {
        combinedSteps[disbursementIndex] = {
          ...combinedSteps[disbursementIndex],
          isActive: true,
          isCompleted: false,
          completionRate: 50,
          status: "inprogress",
          isProvisionalGenerated: selectedApp.isProvisionalGenerated,
          isQualified: selectedApp.isQualified,
          isEligible: selectedApp?.isEligibilityChecked===false ? false : true,
          applicationStatus: selectedApp?.status?.toLowerCase(),
        };
      }
  }
  // Debug: Log status to verify exact value
  console.log("Stepper Status Debug:", selectedApp?.status);
  if(selectedApp?.status?.toLowerCase()==="application closed" || selectedApp?.status?.toLowerCase()==="application rejected"|| selectedApp?.status?.toLowerCase()==="declined by credit"){
     return [
      {
        label: STEP_NAME.ELIGIBILITY_CHECK,
        completionRate: 0,
        status: "pending",
        isActive: false,
        isCompleted: false,
        isProvisionalGenerated: false,
        isQualified: false,
        isEligible: null,
        applicationStatus: selectedApp?.status?.toLowerCase(),
      },
      {
        label: STEP_NAME.APPLICANT_DETAILS,
        completionRate: 0,
        status: "pending",
        isActive: false,
        isCompleted: false,
        isProvisionalGenerated: false,
        isQualified: false,
        isEligible: null,
        applicationStatus: selectedApp?.status?.toLowerCase(),
      },
      {
        label: STEP_NAME.PROVISIONAL_OFFER,
        completionRate: 0,
        status: "pending",
        isActive: false,
        isCompleted: false,
        isProvisionalGenerated: false,
        isQualified: false,
        isEligible: null,
        applicationStatus: selectedApp?.status?.toLowerCase(),
      },
      {
        label: STEP_NAME.ADDITIONAL_DETAILS_AND_DOCUMENTS,
        completionRate: 0,
        status: "pending",
        isActive: false,
        isCompleted: false,
        isProvisionalGenerated: false,
        isQualified: false,
        isEligible: null,
        applicationStatus: selectedApp?.status?.toLowerCase(),
      },
      {
        label: STEP_NAME.FINAL_VERIFICATION_AND_SANCTION,
        completionRate: 0,
        status: "pending",
        isActive: false,
        isCompleted: false,
        isProvisionalGenerated: false,
        isQualified: false,
        isEligible: null,
        applicationStatus: selectedApp?.status?.toLowerCase(),
      },
      {
        label: STEP_NAME.DISBURSEMENT_OF_FUNDS,
        completionRate: 0,
        status: "pending",
        isActive: false,
        isCompleted: false,
        isProvisionalGenerated: false,
        isQualified: false,
        isEligible: null,
        applicationStatus: selectedApp?.status?.toLowerCase(),
      },
    ];
  }
  return combinedSteps;
}

// Detect MIME type from file signature (magic numbers)
const detectMimeTypeFromBytes = (byteArray: Uint8Array): string => {
  // Check first few bytes for file signatures
  const bytes = byteArray.slice(0, 4);

  // PDF: %PDF (25 50 44 46)
  if (
    bytes[0] === 0x25 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x44 &&
    bytes[3] === 0x46
  ) {
    return "application/pdf";
  }

  // PNG: 89 50 4E 47
  if (
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47
  ) {
    return "image/png";
  }

  // JPEG: FF D8 FF
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return "image/jpeg";
  }

  return "application/octet-stream";
};

// Handles any Base64 format
export const decodeBase64FileToUrl = (base64String: string) => {
  if (!base64String) return { url: "", mimeType: "" };

  try {
    // Extract MIME type if present in data URI
    let mimeType = "application/octet-stream";
    if (base64String.includes("data:")) {
      try {
        mimeType = base64String.substring(
          base64String.indexOf(":") + 1,
          base64String.indexOf(";")
        );
      } catch {}
    }

    // Remove base64 prefix and clean all whitespace/newlines
    let cleanedBase64 = base64String.split(",")[1] || base64String;
    cleanedBase64 = cleanedBase64.replace(/\s+/g, "").trim();

    // Decode Base64 to bytes
    const byteCharacters = atob(cleanedBase64);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);

    // If MIME type is still unknown, detect it from file signature
    if (mimeType === "application/octet-stream") {
      mimeType = detectMimeTypeFromBytes(byteArray);
    }

    // Create Blob with detected MIME type
    const fileBlob = new Blob([byteArray], { type: mimeType });

    // Return Object URL and MIME type
    return { url: URL.createObjectURL(fileBlob), mimeType };
  } catch (error) {
    console.error("Error decoding base64 to blob URL:", error);
    return { url: "", mimeType: "" };
  }
};

export const formatName = (name: string = "") => {
  if (!name) return "";
  return String(name)
    .replace(/[\._]/g, " ") // replace . or _ with space
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const autoCompleteOffFields = [
  'pincode',
  'permanentAddressLine1',
  'permanentAddressLine2',
  'permanentAddressLine3', 
  'permanentPincode',
  'employmentCompanyName',
  'employmentCompanyAddress1',
  'employmentCompanyAddress2',
  'employmentCompanyAddress3',
  'employmentLandmark',
  'employmentCompanyCity',
  'employmentCompanyPincode',
  'employmentCompanyState',
  'currentAddressLine1',
  'currentAddressLine2',
  'currentAddressLine3',
  'currentPincode',
  'course1Percentage',
  'course2Percentage',
  'course3Percentage',
  'coreExamScore',
  'languageExamScore',
  'basicCoApplicant1FirstName',
  'basicCoApplicant1LastName',
  'addressCoApplicant1permanentAddressLine1',
  'addressCoApplicant1permanentAddressLine2',
  'addressCoApplicant1permanentAddressLine3',
  'addressCoApplicant1permanentPincode',
  'addressCoApplicant1currentAddressLine1',
  'addressCoApplicant1currentAddressLine2',
  'addressCoApplicant1currentAddressLine3',
  'addressCoApplicant1currentPincode'
];

// A co-applicant's Employment Details save carries its own
// incomeTobeConsideredFlag, derived from Basic Details' payParty answer at
// the moment Employment was last saved. If the applicant later edits Basic
// Details and changes payParty WITHOUT revisiting the Employment tab, that
// flag goes stale on the backend — Employment still reports the OLD
// payParty. This only tells the caller whether a resync is needed; it
// doesn't call anything itself, so it stays reusable from wherever Basic
// Details gets saved independently of Employment.
export const hasCoApplicantPayPartyChanged = (
  coAppNum: string | number,
  currentValues: Record<string, any> | undefined | null,
  previousValues: Record<string, any> | undefined | null,
): boolean => {
  const fieldName = `basicCoApplicant${coAppNum}PayParty`;
  const resolvePayParty = (raw: any): string => {
    const resolved = raw && typeof raw === "object" ? raw.value ?? raw.label : raw;
    return String(resolved ?? "").trim().toLowerCase();
  };
  return (
    resolvePayParty(currentValues?.[fieldName]) !==
    resolvePayParty(previousValues?.[fieldName])
  );
};

// Pulls a co-applicant's basicCoApplicant<N>PayParty value out of the raw
// fetch-application-data response — checking both the "Applicant & Loan
// Details" → "Co-Applicant Details" vertical step (where Basic Details is
// normally captured) and "Additional Details & Documents" → "Co-Applicant
// Additional Details" (in case it was captured/edited there instead).
// Unlike redux's `formData`, this is NOT kept in sync with every
// keystroke/field change — it only reflects what was actually last fetched
// from the backend — so it's the one stable "what was genuinely saved
// before this edit" source hasCoApplicantPayPartyChanged can compare
// against; comparing against `formData` (or Formik `values`) directly
// already reflects the NEW, not-yet-saved answer by the time a save handler
// gets to check it, since those track live edits.
export const getFetchedCoApplicantPayParty = (
  fetchUserApplicationData: any,
  coAppNum: string | number,
): any => {
  const steps = fetchUserApplicationData?.horizontalSteps;
  if (!Array.isArray(steps)) return undefined;

  const fieldName = `basicCoApplicant${coAppNum}PayParty`;

  const applicantLoanStep = steps.find(
    (step: any) => step?.horizontalStepName === "Applicant & Loan Details",
  );
  const documentsStep = steps.find(
    (step: any) =>
      step?.horizontalStepName === "Additional Details & Documents",
  );
  const loanDetailsVStep = applicantLoanStep?.verticalSteps?.find(
    (vStep: any) => vStep?.verticalStepName === "Co-Applicant Details",
  );
  const coApplicantAdditionalDetailsVStep = documentsStep?.verticalSteps?.find(
    (vStep: any) =>
      vStep?.verticalStepName === "Co-Applicant Additional Details" ||
      vStep?.verticalStepName === "Co-Applicant Aditional Details",
  );

  const findInVStep = (vStep: any): any => {
    if (!Array.isArray(vStep?.fields)) return undefined;
    for (const fieldObj of vStep.fields) {
      if (fieldObj && typeof fieldObj === "object" && fieldName in fieldObj) {
        return fieldObj[fieldName];
      }
    }
    return undefined;
  };

  const fromLoanDetails = findInVStep(loanDetailsVStep);
  if (fromLoanDetails !== undefined) return fromLoanDetails;
  return findInVStep(coApplicantAdditionalDetailsVStep);
};

// True when the raw fetch-application-data response already carries a saved
// "Are you employed?" answer for this co-applicant — i.e. Employment Details
// was saved at some point (this or an earlier session), not just in the
// current browser session.
export const isCoApplicantEmploymentSavedInApplicationData = (
  fetchUserApplicationData: any,
  coAppNum: string | number,
): boolean => {
  const steps = fetchUserApplicationData?.horizontalSteps;
  if (!Array.isArray(steps)) return false;

  const employedKeys = [
    `employmentCoApplicant${coAppNum}isEmployed`,
    `employmentCoApplicant${coAppNum}employmentisEmployed`,
  ];
  const isFilled = (raw: any) => {
    const resolved = raw && typeof raw === "object" ? raw.value ?? raw.label : raw;
    return String(resolved ?? "").trim() !== "";
  };

  return steps.some((step: any) =>
    (step?.verticalSteps || []).some((vStep: any) =>
      (Array.isArray(vStep?.fields) ? vStep.fields : []).some(
        (fieldObj: any) =>
          fieldObj &&
          typeof fieldObj === "object" &&
          employedKeys.some((key) => key in fieldObj && isFilled(fieldObj[key])),
      ),
    ),
  );
};